/* ============================================================================
   PawVolt — "Match in a Tap" quiz (v2)
   Self-contained, namespaced (.pvq-*) so it fully supersedes the legacy modal
   quiz in pawvolt.js. Triggers: any [data-pvq-open] element.

   Flow:  Q1 (single router, incl. "a few of these" -> Q2)
          Q2 (multi-select, only for the multi path or "+ add a 2nd focus")
          Result:  N=1  -> single SKU, in-modal Add to Cart (subscribe default),
                          handled by the cart-drawer /cart/add submit interceptor.
                   N>=2 -> bundle, deep-links to /pages/subscribe (Wellness/Total).
          Inline, non-gating email reward -> Klaviyo (same payload as newsletter).

   Data: window.__pawvolt_quiz_skus  (per-concern variant/plan/img/price/blurb)
         window.__pawvolt_quiz_cfg    (klaviyo ids, subscribe page, privacy url)
         Both emitted by snippets/pv-quiz-data.liquid on the homepage.
   ============================================================================ */
(function () {
  'use strict';
  if (window.__pvqInit) return;
  window.__pvqInit = true;

  var SKUS = window.__pawvolt_quiz_skus || {};
  var CFG = window.__pawvolt_quiz_cfg || {};
  var SUBSCRIBE_DEFAULT = true; // single-SKU result preselects Subscribe & save (CFG.subPct)
  var SUBSCRIBE_PAGE = CFG.subscribePage || '/pages/subscribe';
  var SUB_PCT = CFG.subPct || 20; // derived from the real Seal plan in pv-quiz-data.liquid

  var Q1 = [
    { e: '🌿', t: 'Anxiety, stress or fear', v: 'calm' },
    { e: '🦴', t: 'Stiff joints or slowing down', v: 'joint' },
    { e: '🦠', t: 'Tummy issues or loose stools', v: 'gut' },
    { e: '✨', t: 'Itchy skin or dull coat', v: 'skin' },
    { e: '🐾', t: 'Honestly, a few of these', v: 'multi' }
  ];
  var Q2 = [
    { v: 'joint', t: 'Joints & mobility' },
    { v: 'gut', t: 'Gut & digestion' },
    { v: 'skin', t: 'Skin & coat' },
    { v: 'calm', t: 'Calm & stress' },
    { v: 'all', t: 'Everything — full routine' }
  ];
  var ALL = ['joint', 'gut', 'skin', 'calm'];

  var state = newState();
  var overlay = null;
  var lastFocus = null;

  function newState() {
    return { phase: 'q1', primary: null, concerns: [], sub: SUBSCRIBE_DEFAULT, multiFlow: false };
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---- CSS (injected once) ---- */
  var CSS = ''
    + '.pvq-overlay{display:none;position:fixed;inset:0;background:rgba(10,15,30,.72);z-index:10000;align-items:center;justify-content:center;padding:18px;backdrop-filter:blur(6px)}'
    + '.pvq-overlay.open{display:flex}'
    + '.pvq-modal{background:#fff;max-width:520px;width:100%;max-height:92vh;overflow-y:auto;border-radius:18px;padding:clamp(24px,4vw,40px);position:relative;box-shadow:0 24px 70px rgba(0,0,0,.45)}'
    + '.pvq-close{position:absolute;top:14px;right:16px;background:none;border:none;font-size:28px;line-height:1;cursor:pointer;color:#9aa0ab}'
    + '.pvq-close:hover{color:#000}'
    + '.pvq-progress{display:flex;gap:6px;margin:2px 40px 22px 0}'
    + '.pvq-dot{flex:1;height:4px;border-radius:2px;background:#e6e8ec;transition:background .3s}'
    + '.pvq-dot.on{background:var(--color-accent,#c8ff00)}'
    + '.pvq-h{font-family:var(--font-display,inherit);font-size:clamp(20px,2.6vw,26px);line-height:1.2;margin:0 0 6px;color:var(--color-surface,#0a0f1e)}'
    + '.pvq-sub{color:#6b7280;font-size:13.5px;margin:0 0 20px}'
    + '.pvq-options{display:grid;gap:10px}'
    + '.pvq-option{display:flex;align-items:center;gap:14px;padding:15px 16px;border:2px solid #e6e8ec;border-radius:12px;background:#fff;cursor:pointer;text-align:left;font-size:15px;font-weight:600;color:var(--color-surface,#0a0f1e);transition:border-color .15s,background .15s;width:100%}'
    + '.pvq-option:hover{border-color:var(--color-accent,#c8ff00)}'
    + '.pvq-option.sel{border-color:var(--color-accent,#c8ff00);background:#fbffe6}'
    + '.pvq-emoji{font-size:22px;flex:0 0 auto}'
    + '.pvq-check{margin-left:auto;width:20px;height:20px;border-radius:6px;border:2px solid #cbd2da;display:flex;align-items:center;justify-content:center;font-size:13px;color:transparent;flex:0 0 auto}'
    + '.pvq-option.sel .pvq-check{background:var(--color-accent,#c8ff00);border-color:var(--color-accent,#c8ff00);color:var(--color-surface,#0a0f1e)}'
    + '.pvq-nav{display:flex;gap:10px;margin-top:22px}'
    + '.pvq-btn{flex:1;padding:14px 18px;border-radius:10px;font-weight:800;font-size:13px;letter-spacing:.06em;text-transform:uppercase;border:none;cursor:pointer;transition:background .15s}'
    + '.pvq-btn-primary{background:var(--color-surface,#0a0f1e);color:#fff}'
    + '.pvq-btn-primary:hover{background:#000}'
    + '.pvq-btn-primary:disabled{background:#cbd2da;cursor:not-allowed}'
    + '.pvq-btn-back{flex:0 0 auto;background:#fff;border:1px solid #e6e8ec;color:#6b7280}'
    + '.pvq-result{text-align:center}'
    + '.pvq-badge{display:inline-block;background:var(--color-accent,#c8ff00);color:var(--color-surface,#0a0f1e);font-weight:800;font-size:11px;letter-spacing:.12em;text-transform:uppercase;border-radius:999px;padding:6px 14px;margin-bottom:14px}'
    + '.pvq-rimg{width:120px;height:120px;object-fit:contain;margin:0 auto 10px;display:block}'
    + '.pvq-thumbs{display:flex;justify-content:center;gap:8px;margin:0 0 12px;flex-wrap:wrap}'
    + '.pvq-thumbs img{width:64px;height:64px;object-fit:contain;border:1px solid #eee;border-radius:10px;padding:4px;background:#fff}'
    + '.pvq-rtitle{font-family:var(--font-display,inherit);font-size:22px;margin:0 0 6px;color:var(--color-surface,#0a0f1e)}'
    + '.pvq-rblurb{font-size:13.5px;line-height:1.55;color:#6b7280;margin:0 0 16px}'
    + '.pvq-buy{display:flex;gap:8px;margin:0 0 12px}'
    + '.pvq-opt{flex:1;border:2px solid #e6e8ec;border-radius:10px;background:#fff;padding:10px;font-weight:700;font-size:12.5px;color:var(--color-surface,#0a0f1e);cursor:pointer;display:flex;flex-direction:column;gap:2px;align-items:center}'
    + '.pvq-opt span{font-weight:800;font-size:15px}'
    + '.pvq-opt.on{border-color:var(--color-accent,#c8ff00);background:#fbffe6}'
    + '.pvq-trust{font-size:12px;color:#6b7280;margin:0 0 14px;line-height:1.55}'
    + '.pvq-cta{display:block;width:100%;background:var(--color-accent,#c8ff00);color:var(--color-surface,#0a0f1e);font-weight:800;font-size:14px;letter-spacing:.04em;text-transform:uppercase;border:none;border-radius:12px;padding:16px;cursor:pointer;transition:transform .15s}'
    + '.pvq-cta:hover{transform:translateY(-1px)}'
    + '.pvq-link{display:inline-block;margin-top:10px;font-size:13px;color:#6b7280;text-decoration:underline}'
    + '.pvq-secondary{display:block;margin:12px auto 0;background:none;border:none;color:#6b7280;font-size:13px;text-decoration:underline;cursor:pointer}'
    + '.pvq-email{margin-top:18px;padding-top:16px;border-top:1px solid #eef0f3;text-align:left}'
    + '.pvq-email-h{font-weight:700;font-size:13.5px;color:var(--color-surface,#0a0f1e);margin-bottom:8px}'
    + '.pvq-email-form{display:flex;gap:8px}'
    + '.pvq-email-form input{flex:1;border:1px solid #d8dde3;border-radius:9px;padding:11px 12px;font-size:14px;min-width:0}'
    + '.pvq-email-form button{background:var(--color-surface,#0a0f1e);color:#fff;border:none;border-radius:9px;padding:11px 16px;font-weight:700;font-size:13px;cursor:pointer;white-space:nowrap}'
    + '.pvq-email-fb{font-size:12.5px;margin:8px 0 0}'
    + '.pvq-email-fb.ok{color:#2d7a4f}.pvq-email-fb.err{color:#b5341f}'
    + '.pvq-email-fine{font-size:11px;color:#9aa0ab;line-height:1.5;margin:8px 0 0}'
    + '.pvq-email-fine a{color:#6b7280}'
    + '@media (prefers-reduced-motion:reduce){.pvq-cta:hover{transform:none}}';

  function injectCSS() {
    if (document.getElementById('pvq-css')) return;
    var s = document.createElement('style');
    s.id = 'pvq-css';
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  /* ---- modal shell ---- */
  function build() {
    overlay = document.createElement('div');
    overlay.className = 'pvq-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', "Find your dog's formula");
    overlay.innerHTML =
      '<div class="pvq-modal"><button class="pvq-close" type="button" aria-label="Close">&times;</button>'
      + '<div class="pvq-progress" id="pvq-progress"></div><div id="pvq-body"></div></div>';
    document.body.appendChild(overlay);

    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) { close(); return; }
      if (e.target.closest('.pvq-close')) { close(); return; }
      var opt = e.target.closest('.pvq-option');
      if (opt) { onOption(opt.getAttribute('data-val')); return; }
      var act = e.target.closest('[data-act]');
      if (act) { onAct(act.getAttribute('data-act')); return; }
    });
    overlay.addEventListener('submit', function (e) {
      var f = e.target.closest('.pvq-email-form');
      if (f) { e.preventDefault(); submitEmail(f); }
    });
    document.addEventListener('keydown', function (e) {
      if (!overlay || !overlay.classList.contains('open')) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key === 'Tab') {
        var modal = overlay.querySelector('.pvq-modal');
        if (!modal) return;
        var f = modal.querySelectorAll('a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])');
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
  }

  function open() {
    injectCSS();
    if (!overlay) build();
    lastFocus = document.activeElement;
    state = newState();
    render();
    overlay.classList.add('open');
    document.body.style.overflow = 'hidden';
    var c = overlay.querySelector('.pvq-close');
    if (c) c.focus();
  }
  function close() {
    if (!overlay) return;
    overlay.classList.remove('open');
    document.body.style.overflow = '';
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  /* ---- step transitions ---- */
  function onOption(val) {
    if (state.phase === 'q1') {
      if (val === 'multi') {
        state.primary = 'multi';
        state.multiFlow = true;
        state.concerns = [];
        state.phase = 'q2';
      } else {
        state.primary = val;
        state.concerns = [val];
        state.phase = 'result';
      }
      render();
    } else if (state.phase === 'q2') {
      if (val === 'all') {
        state.concerns = (state.concerns.length === ALL.length) ? [] : ALL.slice();
      } else {
        var i = state.concerns.indexOf(val);
        if (i === -1) state.concerns.push(val); else state.concerns.splice(i, 1);
      }
      render();
    }
  }
  function onAct(act) {
    if (act === 'next') {
      if (state.concerns.length) { state.phase = 'result'; render(); }
    } else if (act === 'back') {
      state.phase = 'q1'; render();
    } else if (act === 'sub') {
      state.sub = true; render();
    } else if (act === 'one') {
      state.sub = false; render();
    } else if (act === 'add2') {
      state.multiFlow = true; state.phase = 'q2'; render();
    } else if (act === 'atc') {
      addToCart();
    } else if (act === 'restart') {
      state = newState(); render();
    }
  }

  /* ---- render ---- */
  function flowSteps() { return state.multiFlow ? ['q1', 'q2', 'result'] : ['q1', 'result']; }

  function render() {
    var prog = overlay.querySelector('#pvq-progress');
    var body = overlay.querySelector('#pvq-body');
    var flow = flowSteps();
    var idx = flow.indexOf(state.phase);
    var dots = '';
    for (var d = 0; d < flow.length; d++) dots += '<div class="pvq-dot' + (d <= idx ? ' on' : '') + '"></div>';
    prog.innerHTML = dots;

    if (state.phase === 'q1') body.innerHTML = viewQ1();
    else if (state.phase === 'q2') body.innerHTML = viewQ2();
    else body.innerHTML = viewResult();
  }

  function viewQ1() {
    var h = '<h3 class="pvq-h">What\'s your dog\'s #1 issue right now?</h3>'
      + '<div class="pvq-sub">Pick the one that fits best — we\'ll match a formula in seconds.</div>'
      + '<div class="pvq-options">';
    for (var i = 0; i < Q1.length; i++) {
      var o = Q1[i];
      h += '<button class="pvq-option" type="button" data-val="' + o.v + '"><span class="pvq-emoji">' + o.e + '</span><span>' + esc(o.t) + '</span></button>';
    }
    return h + '</div>';
  }

  function viewQ2() {
    var h = '<h3 class="pvq-h">Which areas should we cover?</h3>'
      + '<div class="pvq-sub">Pick everything that applies — we\'ll build the right plan.</div>'
      + '<div class="pvq-options" role="group" aria-label="Areas to cover">';
    for (var i = 0; i < Q2.length; i++) {
      var o = Q2[i];
      var on = (o.v === 'all') ? (state.concerns.length === ALL.length) : (state.concerns.indexOf(o.v) !== -1);
      h += '<button class="pvq-option' + (on ? ' sel' : '') + '" type="button" aria-pressed="' + on + '" data-val="' + o.v + '"><span>' + esc(o.t) + '</span><span class="pvq-check">✓</span></button>';
    }
    h += '</div><div class="pvq-nav">'
      + '<button class="pvq-btn pvq-btn-back" type="button" data-act="back">Back</button>'
      + '<button class="pvq-btn pvq-btn-primary" type="button" data-act="next"' + (state.concerns.length ? '' : ' disabled') + '>See my plan</button>'
      + '</div>';
    return h;
  }

  function viewResult() {
    var n = state.concerns.length;
    if (n <= 1) return viewSingle(state.concerns[0] || state.primary || 'gut');
    return viewBundle();
  }

  function viewSingle(key) {
    var p = SKUS[key];
    if (!p) return '<div class="pvq-result"><h3 class="pvq-rtitle">Let\'s find the right fit</h3><a class="pvq-cta" href="/collections/dog-supplements">Shop all formulas</a></div>';
    var canSub = !!p.plan;
    if (!canSub) state.sub = false;
    var buy = '';
    if (canSub) {
      buy = '<div class="pvq-buy">'
        + '<button class="pvq-opt' + (state.sub ? ' on' : '') + '" type="button" data-act="sub">Subscribe &amp; save ' + SUB_PCT + '%<span>' + esc(p.subPrice || '') + '/mo</span></button>'
        + '<button class="pvq-opt' + (state.sub ? '' : ' on') + '" type="button" data-act="one">One-time<span>' + esc(p.price || '') + '</span></button>'
        + '</div>';
    } else {
      buy = '<div class="pvq-buy"><button class="pvq-opt on" type="button" disabled>One-time<span>' + esc(p.price || '') + '</span></button></div>';
    }
    var perDay = p.perDay ? ' · about ' + esc(p.perDay) + '/day' : '';
    return '<div class="pvq-result">'
      + '<span class="pvq-badge">Your match</span>'
      + (p.img ? '<img class="pvq-rimg" src="' + esc(p.img) + '" alt="' + esc(p.name) + '">' : '')
      + '<h3 class="pvq-rtitle">' + esc(p.name) + '</h3>'
      + '<p class="pvq-rblurb">' + esc(p.blurb) + '</p>'
      + buy
      + '<div class="pvq-trust">🛡️ 30-day money-back · 🚚 Free shipping · 🔁 Skip or cancel anytime' + perDay + '</div>'
      + (state.sub && canSub ? '<p class="pvq-trust">You authorize a recurring monthly charge until you cancel — skip or cancel anytime.</p>' : '')
      + '<button class="pvq-cta" type="button" data-act="atc">' + (state.sub && canSub ? 'Subscribe &amp; Save ' + SUB_PCT + '%' : 'Add to Cart') + '</button>'
      + '<a class="pvq-link" href="' + esc(p.url) + '">View full details</a>'
      + emailReward()
      + '<button class="pvq-secondary" type="button" data-act="add2">+ Add a 2nd focus</button>'
      + '</div>';
  }

  function viewBundle() {
    var n = state.concerns.length;
    var tier, url;
    if (n >= 4) { tier = 'Total Health'; url = '/products/total-health-monthly-bundle'; }
    else { tier = 'Wellness Pack'; url = '/products/wellness-pack-monthly-bundle'; }
    var thumbs = '';
    for (var i = 0; i < state.concerns.length; i++) {
      var p = SKUS[state.concerns[i]];
      if (p && p.img) thumbs += '<img src="' + esc(p.img) + '" alt="' + esc(p.name) + '">';
    }
    var names = state.concerns.map(function (k) { return SKUS[k] ? SKUS[k].name.replace(' Formula', '') : k; }).join(' + ');
    return '<div class="pvq-result">'
      + '<span class="pvq-badge">Your plan</span>'
      + '<div class="pvq-thumbs">' + thumbs + '</div>'
      + '<h3 class="pvq-rtitle">' + esc(tier) + '</h3>'
      + '<p class="pvq-rblurb">' + esc(names) + ' — covered in one monthly plan, and you save vs buying one-time.</p>'
      + '<div class="pvq-trust">🛡️ 30-day money-back · 🚚 Free shipping · 🔁 Skip, swap or cancel anytime</div>'
      + '<a class="pvq-cta" href="' + esc(url) + '">Build my plan &amp; save</a>'
      + emailReward()
      + '<button class="pvq-secondary" type="button" data-act="restart">Start over</button>'
      + '</div>';
  }

  function emailReward() {
    return '<div class="pvq-email">'
      + '<div class="pvq-email-h">Want 10% off your first order? We\'ll send your code.</div>'
      + '<form class="pvq-email-form" novalidate><input type="email" name="email" placeholder="you@email.com" autocomplete="email" aria-label="Email address" required><button type="submit">Get my code</button></form>'
      + '<p class="pvq-email-fb" role="status" aria-live="polite" hidden></p>'
      + '<p class="pvq-email-fine">By entering your email you agree to receive PawVolt emails (your plan, your 10% code, and dog wellness tips). Unsubscribe anytime.'
      + (CFG.privacyUrl ? ' <a href="' + esc(CFG.privacyUrl) + '">Privacy Policy</a>.' : '')
      + '</p></div>';
  }

  /* ---- add to cart (single SKU) — let the cart-drawer submit interceptor handle it ---- */
  function addToCart() {
    var p = SKUS[state.concerns[0] || state.primary];
    if (!p || !p.variant) { window.location.href = (p && p.url) || '/collections/dog-supplements'; return; }
    var f = document.createElement('form');
    f.action = '/cart/add';
    f.method = 'post';
    f.style.display = 'none';
    var html = '<input name="id" value="' + esc(p.variant) + '"><input name="quantity" value="1">';
    if (state.sub && p.plan) html += '<input name="selling_plan" value="' + esc(p.plan) + '">';
    f.innerHTML = html;
    document.body.appendChild(f);
    close();
    // PawVoltCart's document 'submit' listener (cart-drawer.liquid) intercepts
    // form[action*="/cart/add"], AJAX-adds, and opens the drawer.
    if (f.requestSubmit) f.requestSubmit(); else f.submit();
    setTimeout(function () { if (f.parentNode) f.parentNode.removeChild(f); }, 2500);
  }

  /* ---- email -> Klaviyo (same payload shape as newsletter.liquid) ---- */
  function planTier() {
    var n = state.concerns.length;
    if (n >= 4) return 'total_health';
    if (n >= 2) return 'wellness_pack';
    return 'single';
  }
  function showFb(el, msg, kind) {
    if (!el) return;
    el.textContent = msg; el.hidden = false;
    el.classList.remove('ok', 'err');
    el.classList.add(kind === 'ok' ? 'ok' : 'err');
  }
  function submitEmail(form) {
    var input = form.querySelector('input[name="email"]');
    var fb = form.parentNode.querySelector('.pvq-email-fb');
    var btn = form.querySelector('button[type="submit"]');
    var email = (input.value || '').trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { showFb(fb, 'Please enter a valid email address.', 'err'); input.focus(); return; }
    if (!CFG.klaviyoCompany || !CFG.klaviyoList) { showFb(fb, 'Thanks! We\'ll be in touch.', 'ok'); form.reset(); return; }
    if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'PawVolt Quiz',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                properties: {
                  primary_concern: state.primary,
                  concerns: state.concerns.join(','),
                  plan_tier: planTier()
                }
              }
            }
          }
        },
        relationships: { list: { data: { type: 'list', id: CFG.klaviyoList } } }
      }
    };
    fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(CFG.klaviyoCompany), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (r.status === 202 || r.ok) {
        form.reset(); form.style.display = 'none';
        showFb(fb, "You're in! 🎉 Check your inbox for your 10% off code.", 'ok');
      } else {
        showFb(fb, 'Something went wrong. Please try again.', 'err');
      }
    }).catch(function () {
      showFb(fb, 'Network error. Check your connection and try again.', 'err');
    }).then(function () {
      if (btn) { btn.disabled = false; btn.textContent = 'Get my code'; }
    });
  }

  /* ---- trigger delegation ---- */
  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-pvq-open]');
    if (t) { e.preventDefault(); open(); }
  });
  window.PawVoltQuiz2 = { open: open, close: close };
})();
